import { ReploError } from "schemas/errors";

class CloudflareUploadError extends ReploError {}
export class UploadCanceledError extends ReploError {}

interface SignedUrlResponse {
  signedUrl: string;
  publicUrl: string;
}

interface CloudflareUploadParams {
  file: Blob;
  filename: string;
  projectId: string;
  fileId?: string;
  fetchSignedUrl: (params: {
    projectId: string;
    filename: string;
    fileId?: string;
    contentType: string;
    contentLength: number;
  }) => Promise<SignedUrlResponse>;
  errorContext?: string;
}

export const uploadToCloudflare = async ({
  file,
  filename: originalFilename,
  projectId,
  fileId,
  fetchSignedUrl,
  errorContext = "file",
  onProgress,
  setCancelHandler,
}: CloudflareUploadParams & {
  onProgress?: (loadedBytes: number, totalBytes: number) => void;
  setCancelHandler?: (cancel: () => void) => void;
}): Promise<string> => {
  const { signedUrl, publicUrl } = await fetchSignedUrl({
    projectId,
    filename: originalFilename,
    fileId,
    contentType: file.type,
    contentLength: file.size,
  });

  if (!onProgress) {
    const controller = new AbortController();
    setCancelHandler?.(() => controller.abort());
    try {
      const uploadResponse = await fetch(signedUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
          "Content-Length": file.size.toString(),
        },
        signal: controller.signal,
      });

      if (!uploadResponse.ok) {
        throw new CloudflareUploadError({
          message: `Failed to upload ${errorContext} ${originalFilename} to Cloudflare`,
          additionalData: { status: uploadResponse.status },
        });
      }

      return publicUrl;
    } catch (error) {
      if (
        (error as { name?: string })?.name === "AbortError" ||
        (error as Error)?.message?.toLowerCase?.().includes("abort")
      ) {
        throw new UploadCanceledError({
          message: `Upload of ${errorContext} ${originalFilename} was canceled`,
          additionalData: {
            filename: originalFilename,
            projectId,
            errorContext,
          },
        });
      }
      throw error;
    }
  }

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", signedUrl, true);
    xhr.setRequestHeader("Content-Type", file.type);
    setCancelHandler?.(() => xhr.abort());
    xhr.upload.addEventListener("progress", (event: ProgressEvent) => {
      if (event.lengthComputable) {
        onProgress(event.loaded, event.total);
      } else {
        onProgress(event.loaded, file.size);
      }
    });
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(
          new CloudflareUploadError({
            message: `Failed to upload ${errorContext} ${originalFilename} to Cloudflare`,
            additionalData: { status: xhr.status },
          }),
        );
      }
    });
    xhr.addEventListener("abort", () => {
      reject(
        new UploadCanceledError({
          message: `Upload of ${errorContext} ${originalFilename} was canceled`,
          additionalData: {
            filename: originalFilename,
            projectId,
            errorContext,
          },
        }),
      );
    });
    xhr.addEventListener("error", () => {
      reject(
        new CloudflareUploadError({
          message: `Network error uploading ${errorContext} ${originalFilename}`,
        }),
      );
    });
    xhr.send(file);
  });

  return publicUrl;
};
